// API Configuration — always normalize so a bare Railway host without /api
// (common Netlify env misconfig) cannot break every left-nav feature.
import { resolveApiBaseUrl } from '../lib/apiBaseUrl';
export const API_BASE_URL = resolveApiBaseUrl();

// Stripe Configuration
export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// App Configuration
export const APP_NAME = 'Tripnovac Visa';
export const APP_DESCRIPTION = 'Enterprise Visa Processing Platform — 249 countries and territories (ISO 3166-1)';

// Email Configuration (from environment or defaults)
export const EMAILS = {
  SUPPORT: process.env.REACT_APP_EMAIL_SUPPORT || 'support@tripnovac.com',
  BILLING: process.env.REACT_APP_EMAIL_BILLING || 'billing@tripnovac.com',
  SALES: process.env.REACT_APP_EMAIL_SALES || 'sales@tripnovac.com',
  PARTNERS: process.env.REACT_APP_EMAIL_PARTNERS || 'partners@tripnovac.com',
  NOREPLY: process.env.REACT_APP_EMAIL_NOREPLY || 'noreply@tripnovac.com',
  // Alpha Libra holding company (for legal/terms only)
  COMPANY_INFO: 'info@alphalibrallc.com',
};

// Company Info
export const COMPANY = {
  NAME: 'Alpha Libra LLC',
  LOCATION: 'Wayne, New Jersey',
  PHONE: '1-800-464-0701',
};

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    PROFILE: '/auth/profile',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    LOGOUT: '/auth/logout',
  },
  
  // Countries & Visa Types
  COUNTRIES: '/countries',
  VISA_TYPES: '/visa-types',
  VISA_POLICIES: '/visa-policies',
  VISA_REQUIREMENTS: '/visa-requirements',
  
  // Applications
  APPLICATIONS: '/applications',
  DOCUMENTS: '/documents',
  
  // Payments
  PAYMENTS: '/payments',
  PAYMENT_INTENT: '/payments/create-payment-intent',
  PAYMENT_CHECKOUT: '/payments/create-checkout-session',
  PAYMENT_CONFIRM: '/payments/confirm',
  
  // Dashboard
  DASHBOARD: {
    INDIVIDUAL: '/dashboard/individual',
    CORPORATE: '/dashboard/corporate',
    ADMIN: '/dashboard/admin',
  },
  
  // Admin
  ADMIN: {
    USERS: '/admin/users',
    ANALYTICS: '/admin/analytics',
    BULK_STATUS: '/admin/bulk-status-update',
  },
  
  // Corporate
  CORPORATE: {
    BULK_APPLICATIONS: '/corporate/bulk-applications',
    TEAM_NOTIFY: '/corporate/notify-team',
  },
  
  // Notifications
  NOTIFICATIONS: '/notifications',
  
  // Health
  HEALTH: '/health',
};

// User Roles
export const USER_ROLES = {
  GENERAL: 'general',
  CORPORATE: 'corporate',
  ADMIN: 'admin',
  ENTERPRISE: 'enterprise',
  AGENT: 'agent',
};

// Rate Limits (per hour)
export const RATE_LIMITS = {
  [USER_ROLES.GENERAL]: 100,
  [USER_ROLES.CORPORATE]: 1000,
  [USER_ROLES.ADMIN]: 10000,
  [USER_ROLES.ENTERPRISE]: 5000,
  [USER_ROLES.AGENT]: 2000,
};

// Application Status
export const APPLICATION_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
};

// Visa Types
export const VISA_TYPES = {
  TOURIST: 'tourist',
  BUSINESS: 'business',
  STUDENT: 'student',
  WORK: 'work',
  TRANSIT: 'transit',
  DIPLOMATIC: 'diplomatic',
  FAMILY: 'family',
  HUMANITARIAN: 'humanitarian',
  RETIREMENT: 'retirement',
  SPECIAL: 'special',
};

// Processing Types
export const PROCESSING_TYPES = {
  API: 'api',
  MANUAL: 'manual',
  EMBASSY: 'embassy',
};

// Document Types
export const DOCUMENT_TYPES = {
  PASSPORT: 'passport',
  PHOTO: 'photo',
  BANK_STATEMENT: 'bank_statement',
  EMPLOYMENT_LETTER: 'employment_letter',
  INVITATION_LETTER: 'invitation_letter',
  HOTEL_BOOKING: 'hotel_booking',
  FLIGHT_BOOKING: 'flight_booking',
  INSURANCE: 'insurance',
  EDUCATIONAL_DOCUMENTS: 'educational_documents',
  MARRIAGE_CERTIFICATE: 'marriage_certificate',
  BIRTH_CERTIFICATE: 'birth_certificate',
  OTHER: 'other',
};

// Currencies
export const CURRENCIES = {
  USD: 'USD',
  EUR: 'EUR',
  GBP: 'GBP',
  CAD: 'CAD',
  AUD: 'AUD',
  SGD: 'SGD',
  AED: 'AED',
};

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Theme Colors
export const THEME_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#f57c00',
  SUCCESS: '#4caf50',
  ERROR: '#f44336',
  WARNING: '#ff9800',
  INFO: '#2196f3',
  BACKGROUND: '#f5f5f5',
  PAPER: '#ffffff',
};

// File Upload Limits
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILES: 10,
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.pdf'],
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'tripnovac_auth_token',
  USER_DATA: 'tripnovac_user_data',
  THEME_PREFERENCE: 'tripnovac_theme',
  LANGUAGE_PREFERENCE: 'tripnovac_language',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. Insufficient permissions.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  FILE_TOO_LARGE: 'File size exceeds the maximum limit of 10MB.',
  INVALID_FILE_TYPE: 'Invalid file type. Only JPEG, PNG, and PDF files are allowed.',
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long.',
  PASSWORDS_DONT_MATCH: 'Passwords do not match.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful! Welcome back.',
  REGISTER_SUCCESS: 'Registration successful! Please verify your email.',
  PROFILE_UPDATED: 'Profile updated successfully.',
  APPLICATION_SUBMITTED: 'Application submitted successfully.',
  PAYMENT_SUCCESS: 'Payment processed successfully.',
  DOCUMENT_UPLOADED: 'Document uploaded successfully.',
  PASSWORD_RESET: 'Password reset link sent to your email.',
  EMAIL_VERIFIED: 'Email verified successfully.',
};

// Loading States
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  DISPLAY_LONG: 'MMMM dd, yyyy',
  DISPLAY_WITH_TIME: 'MMM dd, yyyy HH:mm',
  API: 'yyyy-MM-dd',
  API_WITH_TIME: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
};

const constants = {
  API_BASE_URL,
  STRIPE_PUBLISHABLE_KEY,
  APP_NAME,
  APP_DESCRIPTION,
  API_ENDPOINTS,
  USER_ROLES,
  RATE_LIMITS,
  APPLICATION_STATUS,
  VISA_TYPES,
  PROCESSING_TYPES,
  DOCUMENT_TYPES,
  CURRENCIES,
  NOTIFICATION_TYPES,
  THEME_COLORS,
  FILE_UPLOAD,
  PAGINATION,
  STORAGE_KEYS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  LOADING_STATES,
  DATE_FORMATS,
};

export default constants;
